import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useViewer } from '@/entities/viewer';
import { Icons } from '@/shared/ui';

import styles from './Sections.module.css';
import type { SectionId } from './sections';

const POINTS = ['p1', 'p2', 'p3', 'p4'] as const;

/**
 * One capability: a short text with four points beside a large illustration. `flip` puts the
 * illustration on the left; on a narrow screen the text always comes first.
 */
export function FeatureSection({
  id,
  flip = false,
  children,
}: {
  id: SectionId;
  flip?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const heading = `landing-${id}-title`;
  return (
    <section id={id} className={styles.section} aria-labelledby={heading}>
      <div className={`${styles.feature} ${flip ? styles.flip : ''}`}>
        <div className={styles.copy}>
          <p className={styles.eyebrow}>{t(`landing.sections.${id}.eyebrow`)}</p>
          <h2 id={heading} className={styles.title}>
            {t(`landing.sections.${id}.title`)}
          </h2>
          <p className={styles.text}>{t(`landing.sections.${id}.text`)}</p>
          <ul className={styles.points}>
            {POINTS.map((point) => (
              <li key={point}>
                <Icons.check />
                <span>{t(`landing.sections.${id}.points.${point}`)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={styles.visual}>{children}</div>
      </div>
    </section>
  );
}

/** A framed window around an illustration (decorative: it is hidden from assistive technology). */
export function Window({ children, label }: { children: ReactNode; label: string }) {
  return (
    <figure className={styles.window}>
      <div className={styles.windowBody} aria-hidden="true">
        {children}
      </div>
      <figcaption className="visually-hidden">{label}</figcaption>
    </figure>
  );
}

/** Three plain steps, kept short: the page above has already explained the product. */
export function HowItWorks() {
  const { t } = useTranslation();
  const steps = ['s1', 's2', 's3'] as const;
  return (
    <section className={styles.section} aria-labelledby="landing-how-title">
      <h2 id="landing-how-title" className={styles.howTitle}>
        {t('landing.how.title')}
      </h2>
      <ol className={styles.steps}>
        {steps.map((step, index) => (
          <li key={step}>
            <span className={styles.stepNo}>{index + 1}</span>
            <strong>{t(`landing.how.${step}`)}</strong>
            <span className={styles.stepText}>{t(`landing.how.${step}t`)}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function FinalCta() {
  const { t } = useTranslation();
  const viewer = useViewer();
  const signedIn = viewer.status === 'authenticated';
  return (
    <section className={`${styles.section} ${styles.final}`} aria-labelledby="landing-final-title">
      <h2 id="landing-final-title" className={styles.finalTitle}>
        {t('landing.final.title')}
      </h2>
      <p className={styles.finalText}>{t('landing.final.text')}</p>
      <div className={styles.finalActions}>
        {signedIn ? (
          <Link to="/app" className={styles.primary}>
            {t('landing.cta.open')}
          </Link>
        ) : (
          <>
            <Link to="/register" className={styles.primary}>
              {t('landing.cta.start')}
            </Link>
            <Link to="/login" className={styles.secondary}>
              {t('landing.cta.signIn')}
            </Link>
          </>
        )}
      </div>
    </section>
  );
}
